import React from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { Box, Button, Card, CardContent, Stack, Typography } from "@mui/material";
import { BrandLogo } from "./BrandLogo";

const getErrorDetails = (error: unknown) => {
  if (isRouteErrorResponse(error)) {
    return {
      title: `Something went wrong (${error.status})`,
      message:
        error.statusText || "We couldn't load this page right now.",
    };
  }

  if (error instanceof Error) {
    return {
      title: "Unexpected application error",
      message: error.message || "We couldn't load this page right now.",
    };
  }

  return {
    title: "Unexpected application error",
    message: "We couldn't load this page right now.",
  };
};

export const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const { title, message } = getErrorDetails(error);

  if (error) {
    // Ensure the error still shows up in the console for dev debugging.
    // eslint-disable-next-line no-console
    console.error(error);
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "background.default",
        p: 3,
      }}
    >
      <Card
        variant="outlined"
        sx={{ maxWidth: 520, width: "100%", borderColor: "divider" }}
      >
        <CardContent>
          <Stack spacing={2} alignItems="flex-start">
            <BrandLogo />
            <Typography variant="h5" fontWeight={600}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {message}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={() => window.location.reload()}>
                Refresh
              </Button>
              <Button variant="text" onClick={() => window.history.back()}>
                Go back
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
